import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Skeleton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { hostApiClient } from '../../hostApiClient';
import type { HostOrganizationUserResponse, HostUserMonthlyPayment } from '../../models/HostOrganizationUser';
import type { HostStudent, StudentProfile } from '../../models/HostStudent';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import { StudentProfileDialog } from './components/StudentProfileDialog';
import { AddStudentDialog } from './components/AddStudentDialog';
import HostStudentsRoster from './components/HostStudentsRoster';

type HostClientRow = HostOrganizationUserResponse | HostStudent;

function emptyStudentProfile(): StudentProfile {
  return {
    id: null,
    firstName: null,
    lastName: null,
    dateOfBirth: null,
    adult: null,
    guardianContactRequired: false,
    guardianContact: null,
    notes: null,
  };
}

function normalizeHostStudent(row: HostStudent): HostStudent {
  return {
    ...row,
    studentProfile: row.studentProfile ?? emptyStudentProfile(),
  };
}

function isHostStudent(u: HostClientRow): u is HostStudent {
  return 'studentProfile' in u;
}

function schoolProfileLabel(u: HostStudent): string | null {
  const first = u.studentProfile.firstName?.trim() ?? '';
  const last = u.studentProfile.lastName?.trim() ?? '';
  const combined = `${first} ${last}`.trim();
  return combined || null;
}

/** Smaller row actions than default MUI `size="small"` (avoids loud uppercase labels). */
const hostUsersRowActionSx = {
  textTransform: 'none' as const,
  fontSize: '0.6875rem',
  fontWeight: 600,
  py: 0.25,
  px: 1,
  minWidth: 0,
  minHeight: 26,
  lineHeight: 1.2,
};

const usersTablePaperSx = {
  borderRadius: 2,
  /** Must not clip: TableContainer needs horizontal scroll when columns exceed viewport. */
  overflowX: 'auto' as const,
  maxWidth: '100%',
  border: 1,
  borderColor: 'divider',
  boxShadow: 'none',
  backgroundImage: (theme: Theme) =>
    theme.palette.mode === 'dark'
      ? `linear-gradient(165deg, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(
          theme.palette.primary.dark,
          0.12
        )} 100%)`
      : `linear-gradient(165deg, ${theme.palette.background.paper} 0%, ${alpha(
          theme.palette.primary.main,
          0.04
        )} 100%)`,
};

const usersTableHeadRowSx = {
  '& .MuiTableCell-head': {
    fontWeight: 700,
    fontSize: '0.6875rem',
    letterSpacing: '0.07em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
    py: 1.75,
    px: 1.5,
    borderBottom: '2px solid',
    borderColor: 'divider',
    bgcolor: (theme: Theme) =>
      alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.07),
    whiteSpace: 'normal' as const,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
    hyphens: 'auto' as const,
  },
};

function rowStripeBg(theme: Theme, index: number) {
  if (index % 2 === 0) return 'transparent';
  return alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.035);
}

const usersTableSx = {
  minWidth: 880,
  '& .MuiTableCell-root': { px: 1.5, py: 1.25 },
} as const;

const usersActionsHeaderCellSx = { whiteSpace: 'nowrap' as const, width: '1%' };
const usersActionsBodyCellSx = {
  whiteSpace: 'nowrap' as const,
  width: '1%',
  verticalAlign: 'middle' as const,
};

function paymentsLatestFirst(payments: HostUserMonthlyPayment[] | null | undefined): HostUserMonthlyPayment[] {
  if (!payments?.length) return [];
  return [...payments].sort((a, b) => b.month.localeCompare(a.month));
}

function formatMonthMoney(amount: number, currencyCode: string, language: string): string {
  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${currencyCode}`;
  }
}

/** API month `YYYY-MM` → e.g. English "April 2026", Polish "Kwiecień 2026". */
function formatYyyyMmAsMonthYear(yyyyMm: string, language: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(yyyyMm.trim());
  if (!match) return yyyyMm;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11) return yyyyMm;
  const d = new Date(year, monthIndex, 1);
  try {
    const formatted = new Intl.DateTimeFormat(language, {
      month: 'long',
      year: 'numeric',
    }).format(d);
    return formatted.replace(/^./, (ch) => ch.toLocaleUpperCase(language));
  } catch {
    return yyyyMm;
  }
}

export type HostBookingClientsVariant = 'students' | 'room-renters';

interface HostUsersPageProps {
  /** Which booking-client list to load. Defaults to room renters for legacy `/host/users`. */
  variant?: HostBookingClientsVariant;
}

export default function HostUsersPage({ variant = 'room-renters' }: HostUsersPageProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('md'));
  const isStudents = variant === 'students';
  const copyNs = isStudents ? 'hostStudents' : 'hostRoomRenters';

  const [users, setUsers] = useState<HostClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<HostClientRow | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [reportReasonError, setReportReasonError] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const [unblockOpen, setUnblockOpen] = useState(false);
  const [unblockTarget, setUnblockTarget] = useState<HostClientRow | null>(null);
  const [unblockSubmitting, setUnblockSubmitting] = useState(false);

  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<HostStudent | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  const [profileOpen, setProfileOpen] = useState(false);
  const [profileTarget, setProfileTarget] = useState<HostStudent | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      if (isStudents) {
        const data = await hostApiClient.getHostBookingClientsStudents();
        setUsers(data.map(normalizeHostStudent));
      } else {
        setUsers(await hostApiClient.getHostBookingClientsRoomRenters());
      }
    } catch {
      setFetchError(
        t(
          `${copyNs}.fetchError`,
          isStudents ? 'Could not load students.' : 'Could not load room renters.'
        )
      );
    } finally {
      setLoading(false);
    }
  }, [t, isStudents, copyNs]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const displayName = (u: HostClientRow) =>
    u.name?.trim() || u.phoneNumber || t('hostUsers.unnamedUser', 'Unnamed user');

  const formatWhen = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  const formatDob = (yyyyMmDd: string | null) => {
    if (!yyyyMmDd) return null;
    try {
      return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
        new Date(`${yyyyMmDd}T00:00:00`)
      );
    } catch {
      return yyyyMmDd;
    }
  };

  const latestSpendLine = (u: HostClientRow): string | null => {
    const sorted = paymentsLatestFirst(u.monthlyPayments ?? undefined);
    if (!sorted.length) return null;
    const p = sorted[0];
    return `${formatYyyyMmAsMonthYear(p.month, i18n.language)} · ${formatMonthMoney(p.totalAmount, p.currencyCode, i18n.language)}`;
  };

  const moderationChips = (u: HostClientRow) => {
    const m = u.moderationWarning;
    if (!m) return null;

    if (m.globallyBlacklisted) {
      return (
        <Chip
          size="small"
          color="error"
          label={t('hostUsers.badge.globalBlacklist', 'Globally blacklisted')}
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (m.currentOrganizationBlacklisted) {
      return (
        <Chip
          size="small"
          color="error"
          label={t('hostUsers.badge.orgBlacklist', 'Blacklisted in your organization')}
          sx={{ fontWeight: 600 }}
        />
      );
    }
    if (m.warningVisible && m.reportingOrganizationCount > 0) {
      const n = m.reportingOrganizationCount;
      const label =
        n === 1
          ? t('hostUsers.badge.reportedByOne', 'Reported by 1 organization')
          : t('hostUsers.badge.reportedByMany', 'Reported by {{count}} organizations', { count: n });
      return (
        <Chip size="small" color="warning" label={label} sx={{ fontWeight: 600 }} />
      );
    }
    return null;
  };

  const bookingsBlock = (u: HostClientRow) => {
    const count = isStudents
      ? (u.tutorBookingCount ?? 0)
      : 'roomBookingCount' in u
        ? (u.roomBookingCount ?? 0)
        : 0;
    return (
      <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.25}>
        <Typography variant="body2" fontWeight={600}>
          {t(
            `${copyNs}.bookingsCount`,
            isStudents ? '{{count}} lessons' : '{{count}} bookings',
            { count }
          )}
        </Typography>
      </Stack>
    );
  };

  const studentProfileChips = (u: HostStudent) => {
    const profile = u.studentProfile;
    const chips: React.ReactNode[] = [];
    if (profile.adult === false) {
      chips.push(
        <Chip
          key="minor"
          size="small"
          color={profile.guardianContactRequired && !profile.guardianContact?.trim() ? 'warning' : 'default'}
          label={
            profile.guardianContactRequired && !profile.guardianContact?.trim()
              ? t('hostStudents.profile.minorMissingGuardian', 'Under 18 · guardian needed')
              : t('hostStudents.profile.minor', 'Under 18')
          }
          sx={{ fontWeight: 600 }}
        />
      );
    } else if (profile.adult === true) {
      chips.push(
        <Chip
          key="adult"
          size="small"
          variant="outlined"
          label={t('hostStudents.profile.adult', 'Adult')}
          sx={{ fontWeight: 600 }}
        />
      );
    } else if (!profile.firstName && !profile.lastName && !profile.dateOfBirth) {
      chips.push(
        <Chip
          key="incomplete"
          size="small"
          variant="outlined"
          label={t('hostStudents.profile.incomplete', 'Profile incomplete')}
          sx={{ fontWeight: 600 }}
        />
      );
    }
    return chips.length ? (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
        {chips}
      </Stack>
    ) : null;
  };

  const monthlyHistoryBlock = (u: HostClientRow) => {
    const sorted = paymentsLatestFirst(u.monthlyPayments ?? undefined);
    if (!sorted.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('hostUsers.noMonthlySpend', 'No confirmed spend recorded.')}
        </Typography>
      );
    }
    return (
      <Stack spacing={0.75}>
        {sorted.map((p) => (
          <Typography key={`${u.id}-${p.month}-${p.currencyCode}`} variant="body2">
            {formatYyyyMmAsMonthYear(p.month, i18n.language)} ·{' '}
            {formatMonthMoney(p.totalAmount, p.currencyCode, i18n.language)}
          </Typography>
        ))}
      </Stack>
    );
  };

  const openReport = (u: HostClientRow) => {
    setReportTarget(u);
    setReportReason('');
    setReportReasonError(false);
    setReportOpen(true);
  };

  const openProfile = (u: HostStudent) => {
    setProfileTarget(u);
    setProfileOpen(true);
  };

  const handleProfileSaved = (studentUserId: string, profile: StudentProfile) => {
    setUsers((prev) =>
      prev.map((row) =>
        row.id === studentUserId && isHostStudent(row)
          ? { ...row, studentProfile: profile }
          : row
      )
    );
    setSnackbar({
      open: true,
      message: t('hostStudents.profile.saveSuccess', 'Student profile saved.'),
      severity: 'success',
    });
  };

  const handleStudentAdded = () => {
    setSnackbar({
      open: true,
      message: t('hostStudents.add.success', 'Student added.'),
      severity: 'success',
    });
    void loadUsers();
  };

  const submitReport = async () => {
    if (!reportTarget) return;
    const trimmed = reportReason.trim();
    if (!trimmed) {
      setReportReasonError(true);
      return;
    }
    setReportReasonError(false);
    setReportSubmitting(true);
    try {
      await hostApiClient.reportHostUser(reportTarget.id, trimmed);
      setReportOpen(false);
      setReportTarget(null);
      setReportReason('');
      setSnackbar({
        open: true,
        message: t('hostUsers.reportSuccess', 'User reported and blocked in your organization.'),
        severity: 'success',
      });
      await loadUsers();
    } catch {
      setSnackbar({
        open: true,
        message: t('hostUsers.reportError', 'Could not submit report. Try again.'),
        severity: 'error',
      });
    } finally {
      setReportSubmitting(false);
    }
  };

  const openUnblock = (u: HostClientRow) => {
    setUnblockTarget(u);
    setUnblockOpen(true);
  };

  const openRemove = (u: HostStudent) => {
    setRemoveTarget(u);
    setRemoveOpen(true);
  };

  const submitRemove = async () => {
    if (!removeTarget) return;
    setRemoveSubmitting(true);
    try {
      await hostApiClient.removeHostStudent(removeTarget.id);
      setRemoveOpen(false);
      setRemoveTarget(null);
      setSnackbar({
        open: true,
        message: t('hostStudents.remove.success', 'Student removed from your school.'),
        severity: 'success',
      });
      await loadUsers();
    } catch {
      setSnackbar({
        open: true,
        message: t('hostStudents.remove.error', 'Could not remove student. Try again.'),
        severity: 'error',
      });
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const submitUnblock = async () => {
    if (!unblockTarget) return;
    setUnblockSubmitting(true);
    try {
      await hostApiClient.removeHostUserOrganizationBlacklist(unblockTarget.id);
      setUnblockOpen(false);
      setUnblockTarget(null);
      setSnackbar({
        open: true,
        message: t('hostUsers.unblockSuccess', 'User removed from your organization blacklist.'),
        severity: 'success',
      });
      await loadUsers();
    } catch {
      setSnackbar({
        open: true,
        message: t('hostUsers.unblockError', 'Could not remove blacklist. Try again.'),
        severity: 'error',
      });
    } finally {
      setUnblockSubmitting(false);
    }
  };

  const canReport = (u: HostClientRow) => {
    const m = u.moderationWarning;
    if (m?.globallyBlacklisted) return false;
    if (m?.currentOrganizationBlacklisted) return false;
    return true;
  };

  const canUnblock = (u: HostClientRow) =>
    Boolean(u.moderationWarning?.currentOrganizationBlacklisted);

  /** Students: no expand / monthly-spend / moderation columns. Room renters: full table. */
  const tableColCount = isStudents ? 6 : 9;
  const latestColumnLabel = isStudents
    ? t('hostStudents.col.latest', 'Latest lesson')
    : t('hostUsers.col.latest', 'Latest booking');
  const latestFieldLabel = isStudents
    ? t('hostStudents.latestLesson', 'Latest lesson')
    : t('hostUsers.latestBooking', 'Latest booking');

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 1400,
        mx: 'auto',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {isStudents ? (
        <HostStudentsRoster
          students={users.filter(isHostStudent)}
          loading={loading}
          fetchError={fetchError}
          onDismissError={() => setFetchError(null)}
          onRetry={() => void loadUsers()}
          onAdd={() => setAddOpen(true)}
          onEdit={openProfile}
          onReport={openReport}
          onUnblock={openUnblock}
          onRemove={openRemove}
          canReport={canReport}
          canUnblock={canUnblock}
        />
      ) : (
        <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          pb: 2.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ maxWidth: 720, minWidth: 0, flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 0.75 }}>
            {t(
              `${copyNs}.title`,
              isStudents ? 'Students' : 'Room renters'
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              `${copyNs}.subtitle`,
              isStudents
                ? 'People with tutor lesson bookings in your organization, plus students you add. Maintain school profiles (including guardian contact for minors) and report misuse.'
                : 'People with rehearsal room bookings in your organization. You can report misuse, see confirmed monthly spend, and manage your organization blacklist.'
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {!loading && users.length > 0 && (
            <Chip
              label={t(
                `${copyNs}.countLabel`,
                isStudents ? '{{count}} students' : '{{count}} room renters',
                { count: users.length }
              )}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 700, borderRadius: 2, px: 0.5 }}
            />
          )}
          {isStudents && (
            <Tooltip title={t('hostStudents.add.title', 'Add student')}>
              <IconButton
                color="primary"
                onClick={() => setAddOpen(true)}
                aria-label={t('hostStudents.add.title', 'Add student')}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  width: 36,
                  height: 36,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {fetchError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setFetchError(null)}
          action={
            <Button color="inherit" size="small" onClick={() => loadUsers()}>
              {t('hostUsers.retry', 'Retry')}
            </Button>
          }
        >
          {fetchError}
        </Alert>
      )}

      {loading ? (
        isSmDown ? (
          <Stack spacing={2}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Skeleton variant="circular" width={52} height={52} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="55%" height={28} />
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="text" width="70%" sx={{ mt: 1 }} />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={usersTablePaperSx}>
            <Table size="medium" sx={usersTableSx}>
              <TableHead>
                <TableRow sx={usersTableHeadRowSx}>
                  {!isStudents && <TableCell padding="checkbox" />}
                  <TableCell>{t('hostUsers.col.user', 'User')}</TableCell>
                  <TableCell>{t('hostUsers.col.phone', 'Phone')}</TableCell>
                  <TableCell>
                    {isStudents
                      ? t('hostStudents.col.profile', 'School profile')
                      : t('hostUsers.col.band', 'Band')}
                  </TableCell>
                  <TableCell align="right">
                    {t(
                      `${copyNs}.col.bookings`,
                      isStudents ? 'Lessons' : 'Bookings'
                    )}
                  </TableCell>
                  {!isStudents && (
                    <TableCell>{t('hostUsers.col.monthlySpend', 'Monthly spend')}</TableCell>
                  )}
                  <TableCell>{latestColumnLabel}</TableCell>
                  {!isStudents && (
                    <TableCell>{t('hostUsers.col.moderation', 'Moderation')}</TableCell>
                  )}
                  <TableCell align="right" sx={usersActionsHeaderCellSx}>
                    {t('hostUsers.col.actions', 'Actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: tableColCount }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" width={j === 0 && !isStudents ? 24 : '90%'} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : fetchError ? null : users.length === 0 ? (
        <Paper
          variant="outlined"
          sx={(muiTheme) => ({
            p: { xs: 4, sm: 6 },
            textAlign: 'center',
            borderRadius: 3,
            borderStyle: 'dashed',
            bgcolor: alpha(muiTheme.palette.primary.main, muiTheme.palette.mode === 'dark' ? 0.06 : 0.03),
          })}
        >
          <PeopleOutlineIcon
            sx={{ fontSize: 52, color: 'text.secondary', opacity: 0.55, mb: 1.5 }}
            aria-hidden
          />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            {t(
              `${copyNs}.emptyTitle`,
              isStudents ? 'No students yet' : 'No room renters yet'
            )}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
            {t(
              `${copyNs}.emptyHint`,
              isStudents
                ? 'Add a student by phone number, or they will appear here after they book tutor lessons in your organization.'
                : 'Room renters appear here after they book rehearsal rooms in your organization.'
            )}
          </Typography>
          {isStudents && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              sx={{ mt: 2.5, textTransform: 'none', fontWeight: 700 }}
            >
              {t('hostStudents.add.title', 'Add student')}
            </Button>
          )}
        </Paper>
      ) : isSmDown ? (
        <Stack spacing={2}>
          {users.map((u) => {
            const expanded = expandedIds.has(u.id);
            const hasHistory = (u.monthlyPayments?.length ?? 0) > 0;
            return (
              <Card
                key={u.id}
                variant="outlined"
                sx={(muiTheme) => ({
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    borderColor: alpha(muiTheme.palette.primary.main, 0.45),
                    boxShadow: `0 10px 28px ${alpha(muiTheme.palette.common.black, muiTheme.palette.mode === 'dark' ? 0.35 : 0.08)}`,
                  },
                })}
              >
                <CardContent sx={{ '&:last-child': { pb: 2 }, pt: 2.25 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      src={u.profileImageUrl || undefined}
                      sx={{
                        width: 52,
                        height: 52,
                        fontWeight: 700,
                        boxShadow: (muiTheme) => `0 4px 14px ${alpha(muiTheme.palette.common.black, 0.15)}`,
                      }}
                    >
                      {displayName(u).charAt(0).toUpperCase()}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography fontWeight={700} noWrap>
                        {displayName(u)}
                      </Typography>
                      {isHostStudent(u) ? (
                        <>
                          {schoolProfileLabel(u) && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {schoolProfileLabel(u)}
                            </Typography>
                          )}
                          {u.studentProfile.dateOfBirth && (
                            <Typography variant="body2" color="text.secondary">
                              {t('hostStudents.profile.born', 'Born {{date}}', {
                                date: formatDob(u.studentProfile.dateOfBirth),
                              })}
                            </Typography>
                          )}
                          {studentProfileChips(u)}
                        </>
                      ) : (
                        u.bandName && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {u.bandName}
                          </Typography>
                        )
                      )}
                      {u.phoneNumber && (
                        <Typography variant="body2" color="text.secondary">
                          {u.phoneNumber}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1 }}>{bookingsBlock(u)}</Box>
                      {!isStudents && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {t('hostUsers.col.monthlySpend', 'Monthly spend')}:{' '}
                          {latestSpendLine(u) ?? '—'}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {latestFieldLabel}: {formatWhen(u.latestBookingAt)}
                      </Typography>
                      {!isStudents && <Box sx={{ mt: 1 }}>{moderationChips(u)}</Box>}
                      {!isStudents && hasHistory && (
                        <>
                          <Button
                            size="small"
                            onClick={() => toggleExpanded(u.id)}
                            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ mt: 1, textTransform: 'none', p: 0, minWidth: 0 }}
                          >
                            {t('hostUsers.monthlyHistory', 'Payment history')}
                          </Button>
                          <Collapse in={expanded}>
                            <Box sx={{ mt: 1, pl: 0.5 }}>{monthlyHistoryBlock(u)}</Box>
                          </Collapse>
                        </>
                      )}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        {isHostStudent(u) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openProfile(u)}
                            sx={hostUsersRowActionSx}
                          >
                            {t('hostStudents.profile.edit', 'Edit profile')}
                          </Button>
                        )}
                        {canReport(u) && (
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            onClick={() => openReport(u)}
                            sx={hostUsersRowActionSx}
                          >
                            {t('hostUsers.reportUser', 'Report user')}
                          </Button>
                        )}
                        {canUnblock(u) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openUnblock(u)}
                            sx={hostUsersRowActionSx}
                          >
                            {t('hostUsers.removeBlacklist', 'Remove blacklist')}
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={usersTablePaperSx}>
          <Table size="medium" sx={usersTableSx}>
            <TableHead>
              <TableRow sx={usersTableHeadRowSx}>
                {!isStudents && <TableCell padding="checkbox" sx={{ width: 48 }} />}
                <TableCell>{t('hostUsers.col.user', 'User')}</TableCell>
                <TableCell>{t('hostUsers.col.phone', 'Phone')}</TableCell>
                <TableCell>
                  {isStudents
                    ? t('hostStudents.col.profile', 'School profile')
                    : t('hostUsers.col.band', 'Band')}
                </TableCell>
                <TableCell align="right">
                  {t(
                    `${copyNs}.col.bookings`,
                    isStudents ? 'Lessons' : 'Bookings'
                  )}
                </TableCell>
                {!isStudents && (
                  <TableCell>{t('hostUsers.col.monthlySpend', 'Monthly spend')}</TableCell>
                )}
                <TableCell sx={{ maxWidth: 168 }}>{latestColumnLabel}</TableCell>
                {!isStudents && (
                  <TableCell>{t('hostUsers.col.moderation', 'Moderation')}</TableCell>
                )}
                <TableCell align="right" sx={usersActionsHeaderCellSx}>
                  {t('hostUsers.col.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u, userIndex) => {
                const expanded = expandedIds.has(u.id);
                const hasMultipleMonths = (u.monthlyPayments?.length ?? 0) > 1;
                const student = isHostStudent(u) ? u : null;
                return (
                  <React.Fragment key={u.id}>
                    <TableRow
                      hover
                      sx={(muiTheme) => ({
                        bgcolor: rowStripeBg(muiTheme, userIndex),
                        transition: 'background-color 0.15s ease',
                      })}
                    >
                      {!isStudents && (
                        <TableCell padding="checkbox">
                          {(u.monthlyPayments?.length ?? 0) > 0 ? (
                            <IconButton
                              size="small"
                              aria-label={expanded ? 'Collapse' : 'Expand'}
                              onClick={() => toggleExpanded(u.id)}
                            >
                              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          ) : null}
                        </TableCell>
                      )}
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={u.profileImageUrl || undefined}
                            sx={{ width: 40, height: 40, fontWeight: 700 }}
                          >
                            {displayName(u).charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={700}>{displayName(u)}</Typography>
                            {student && studentProfileChips(student)}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{u.phoneNumber || '—'}</TableCell>
                      <TableCell>
                        {student ? (
                          <Stack spacing={0.25}>
                            <Typography variant="body2">
                              {schoolProfileLabel(student) || '—'}
                            </Typography>
                            {student.studentProfile.dateOfBirth && (
                              <Typography variant="caption" color="text.secondary">
                                {formatDob(student.studentProfile.dateOfBirth)}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          u.bandName || '—'
                        )}
                      </TableCell>
                      <TableCell align="right">{bookingsBlock(u)}</TableCell>
                      {!isStudents && (
                        <TableCell>
                          <Typography variant="body2">{latestSpendLine(u) ?? '—'}</Typography>
                          {hasMultipleMonths && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('hostUsers.expandForHistory', 'Expand row for full history')}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={{ maxWidth: 168, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {formatWhen(u.latestBookingAt)}
                      </TableCell>
                      {!isStudents && <TableCell>{moderationChips(u)}</TableCell>}
                      <TableCell align="right" sx={usersActionsBodyCellSx}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          flexWrap="nowrap"
                          sx={{ minWidth: 'max-content' }}
                        >
                          {student && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openProfile(student)}
                              sx={hostUsersRowActionSx}
                            >
                              {t('hostStudents.profile.edit', 'Edit profile')}
                            </Button>
                          )}
                          {canReport(u) && (
                            <Button
                              size="small"
                              color="warning"
                              variant="outlined"
                              onClick={() => openReport(u)}
                              sx={hostUsersRowActionSx}
                            >
                              {t('hostUsers.reportUser', 'Report user')}
                            </Button>
                          )}
                          {canUnblock(u) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openUnblock(u)}
                              sx={hostUsersRowActionSx}
                            >
                              {t('hostUsers.removeBlacklist', 'Remove blacklist')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {!isStudents && (
                      <TableRow
                        sx={(muiTheme) => ({
                          bgcolor: rowStripeBg(muiTheme, userIndex),
                        })}
                      >
                        <TableCell
                          colSpan={tableColCount}
                          sx={{
                            py: 0,
                            borderBottom: expanded ? 1 : 0,
                            borderColor: 'divider',
                            px: 0,
                          }}
                        >
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={(muiTheme) => ({
                                py: 2.5,
                                px: 2.5,
                                mx: 2,
                                mb: expanded ? 2 : 0,
                                borderLeft: '3px solid',
                                borderColor: 'primary.main',
                                borderRadius: 1,
                                bgcolor: alpha(
                                  muiTheme.palette.primary.main,
                                  muiTheme.palette.mode === 'dark' ? 0.08 : 0.05
                                ),
                              })}
                            >
                              <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{ display: 'block', letterSpacing: '0.08em', fontWeight: 700, mb: 1 }}
                              >
                                {t('hostUsers.monthlyHistoryTitle', 'Confirmed monthly spend')}
                              </Typography>
                              {monthlyHistoryBlock(u)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
        </>
      )}

      <Dialog open={reportOpen} onClose={() => !reportSubmitting && setReportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('hostUsers.reportTitle', 'Report and block user')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              'hostUsers.reportExplainer',
              'This will block the user from booking spaces in your organization.'
            )}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('hostUsers.reasonLabel', 'Reason')}
            placeholder={t('hostUsers.reasonPlaceholder', 'Describe what happened')}
            fullWidth
            multiline
            minRows={4}
            value={reportReason}
            onChange={(e) => {
              setReportReason(e.target.value);
              if (reportReasonError && e.target.value.trim()) setReportReasonError(false);
            }}
            error={reportReasonError}
            helperText={reportReasonError ? t('hostUsers.reasonRequired', 'Reason is required.') : undefined}
            disabled={reportSubmitting}
            sx={(muiTheme) => ({
              mt: 0.5,
              '& .MuiOutlinedInput-root': {
                backgroundColor:
                  muiTheme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : muiTheme.palette.grey[100],
                alignItems: 'flex-start',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  muiTheme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.45)'
                    : muiTheme.palette.grey[400],
                borderWidth: '1px',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  muiTheme.palette.mode === 'dark'
                    ? 'rgba(129, 140, 248, 0.9)'
                    : muiTheme.palette.primary.main,
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '& .MuiInputBase-input': {
                color: 'text.primary',
                py: 1.25,
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'text.secondary',
                opacity: 0.85,
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main',
              },
              '& .MuiFormHelperText-root': {
                mt: 0.75,
              },
            })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)} disabled={reportSubmitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="warning" onClick={submitReport} disabled={reportSubmitting}>
            {reportSubmitting ? <CircularProgress size={22} /> : t('hostUsers.reportConfirm', 'Report user')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={unblockOpen} onClose={() => !unblockSubmitting && setUnblockOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('hostUsers.unblockTitle', 'Remove blacklist')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              'hostUsers.unblockMessage',
              'This will allow the user to book in your organization again. Previous reports will remain visible to admins.'
            )}
          </Typography>
          {unblockTarget && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={600}>{displayName(unblockTarget)}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnblockOpen(false)} disabled={unblockSubmitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={submitUnblock} disabled={unblockSubmitting}>
            {unblockSubmitting ? (
              <CircularProgress size={22} />
            ) : (
              t('hostUsers.unblockConfirm', 'Unblock user')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removeOpen} onClose={() => !removeSubmitting && setRemoveOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('hostStudents.remove.title', 'Remove from school')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              'hostStudents.remove.message',
              'This removes the student from your school roster and school profile. Their app account and past lesson history stay intact.'
            )}
          </Typography>
          {removeTarget && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={600}>
                {schoolProfileLabel(removeTarget) || displayName(removeTarget)}
              </Typography>
              {removeTarget.phoneNumber && (
                <Typography variant="body2" color="text.secondary">
                  {removeTarget.phoneNumber}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOpen(false)} disabled={removeSubmitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={() => void submitRemove()} disabled={removeSubmitting}>
            {removeSubmitting ? (
              <CircularProgress size={22} />
            ) : (
              t('hostStudents.remove.confirm', 'Remove from school')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <StudentProfileDialog
        open={profileOpen}
        student={profileTarget}
        onClose={() => {
          setProfileOpen(false);
          setProfileTarget(null);
        }}
        onSaved={handleProfileSaved}
      />

      {isStudents && (
        <AddStudentDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onAdded={handleStudentAdded}
        />
      )}

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}
